/**
 * ADO #587 — words-only mode (the Problem Solving course).
 *
 * ONE switch: useConfigStore().uiMode.showGeneratedCode. false = words only —
 * no generated code, no code editor, no C on the keys; the builder shows words
 * and the flow. The expression keyboard already honours it
 * (ExpressionKeyboard/useKeyboardEnv.js → useKeyboardCourseMode). The builder
 * page sets it from the item's /progress response (`words_only`), so the C
 * Programming course and an item outside any course keep words + C.
 *
 * The values stay C: only what the learner READS changes. A variable is still
 * stored as `int`; the picker just says "whole number".
 */
import useConfigStore from '@/stores/useConfigStore';

/** True when the builder must show words only. */
export function useWordsOnly() {
  return useConfigStore((s) => s.uiMode?.showGeneratedCode === false);
}

/** Non-hook read, for callbacks. */
export function isWordsOnly() {
  return useConfigStore.getState().uiMode?.showGeneratedCode === false;
}

/**
 * The /progress response says whether the item is words only. The preview
 * frontend talks to the production backend, which may not send the field yet:
 * a missing or malformed field is "not words only".
 */
export function wordsOnlyFromProgress(data) {
  return !!(data && data.words_only === true);
}

/**
 * A safe way to SEE words-only mode before the backend ships: `?dev=true&wordsOnly=1`.
 * Both are needed, so a stray `wordsOnly` on a learner's link does nothing.
 */
export function wordsOnlyFromDevQuery(searchParams) {
  if (!searchParams || typeof searchParams.get !== 'function') return false;
  return searchParams.get('dev') === 'true' && searchParams.get('wordsOnly') === '1';
}

// The kinds of value a Problem Solving learner names, in words.
export const C_TYPE_WORDS = {
  int: 'whole number',
  float: 'decimal',
  double: 'decimal (more digits)',
  char: 'character',
  bool: 'true / false',
  void: 'nothing',
  long: 'big whole number',
  'long long': 'big whole number',
  'unsigned int': 'whole number (0 or more)',
  size_t: 'count',
  FILE: 'file',
};

// The common ones the variable form offers in words-only mode. The rest of
// the C list (short, unsigned long long, int8_t, va_list…) is hidden.
export const WORDS_ONLY_DATA_TYPES = ['int', 'float', 'double', 'char', 'bool'];

/** A C type as words, or the type itself when there is no word for it. */
export function cTypeInWords(type) {
  const t = String(type || '').trim();
  if (C_TYPE_WORDS[t]) return C_TYPE_WORDS[t];
  const struct = t.match(/^(?:struct|union)[\s_]+(\w+)$/);
  if (struct) return struct[1];
  return t;
}

/**
 * A composed C type (`const char*`, `int[]`) described in words. Used where
 * the builder shows a type the learner picked from a guided control.
 */
export function describeCTypeInWords({ isConst, depth = 0, base, dims } = {}) {
  let out = cTypeInWords(base || 'int');
  if (depth > 0) out = `address of ${out}${depth > 1 ? ` (${depth} levels)` : ''}`;
  if (dims && dims.length === 1) out = `list of ${out}`;
  if (dims && dims.length === 2) out = `table of ${out}`;
  if (isConst) out = `${out}, read-only`;
  return out;
}

/**
 * The data-type list for the variable / array forms in words-only mode: the
 * common types in words, plus the learner's own types (structs, enums), plus
 * whatever a row already holds so an existing `long` still shows.
 */
export function wordsOnlyDataTypes(dataTypes, keepValues = []) {
  const list = Array.isArray(dataTypes) ? dataTypes : [];
  const keep = new Set((keepValues || []).filter(Boolean));
  return list
    .filter((dt) => dt && (WORDS_ONLY_DATA_TYPES.includes(dt.value) || dt.isUserDefined || keep.has(dt.value)))
    .map((dt) => ({ ...dt, label: dt.isUserDefined ? cTypeInWords(dt.label) : cTypeInWords(dt.value) }));
}

// The output template's format specs, in words (no %d / printf).
export const FORMAT_SPEC_WORDS = {
  d: 'whole number',
  '.2f': 'decimal, 2 places',
  '.0f': 'decimal, no places',
  f: 'decimal',
  x: 'hexadecimal',
  o: 'octal',
  c: 'one character',
  s: 'text',
  e: 'scientific',
};

/**
 * The full data-type list with the common types relabelled in words. For
 * pickers where hiding a type would block an item (a pointer to a FILE in the
 * files module), so nothing is removed.
 */
export function relabelDataTypesInWords(dataTypes) {
  return (Array.isArray(dataTypes) ? dataTypes : [])
    .map((dt) => (dt ? { ...dt, label: dt.isUserDefined ? cTypeInWords(dt.label) : cTypeInWords(dt.value) } : dt));
}
